import * as THREE from "three";
import { TANK, BULLET, LIVES_START, PLAYER_COLOR, CAMERA, SIM_SPEED, SHELL_COLOR, TANK_TYPES, CARD_MODE } from "./config";
import { Input } from "./engine/input";
import { log, debug } from "./engine/debug";
import { sfx } from "./engine/sfx";
import { startShell, shellChanged, inShell } from "./shell";
import { music } from "./engine/music";
import { Confetti } from "./ui/confetti";
import { World } from "./world/scene";
import {
  GameMap, layerLayout,
  isOverHole, resolveCircle, clampInterior, setDynamicWalls, setLayerBounds,
} from "./world/gamemap";
import type { Arena } from "./world/arena";
import { loadTankGeometries } from "./tank/prefab";
import { Guidebook } from "./ui/guidebook";
import { Tank, yawToward, approachAngle } from "./entities/tank";
import { Bullets, type HitEvent } from "./systems/bullets";
import { EnemyManager } from "./systems/enemies";
import { Effects } from "./systems/effects";
import { Treads, TREAD_STEP2 } from "./systems/treads";
import { DeathMarks } from "./systems/deathmarks";
import { CardDeck } from "./systems/cards";
import { AimLine } from "./systems/aimline";
import { Hud, type HudState } from "./ui/hud";
import { DevPanel } from "./ui/dev";
import * as atproto from "./data/atproto";
import { ProfileStore, emptyProfile } from "./data/profile";
import { loadGameState, saveGameState, clearGameState, type SavedGameState } from "./data/gamestate";
import { AchievementSystem } from "./data/achievements";
import { fetchMutualFaces } from "./data/bsky";
import {
  rollDraft, applyUpgrade, defaultLevers, UPGRADES,
  type UpgradeLevers, type UpgradeContext, type UpgradeDef,
} from "./systems/upgrades";

type Mode = "menu" | "playing" | "gameover";
type Phase = "deploy" | "combat" | "cleared";

/** The one place a flair is chosen, for every game. */
const PICKER_URL = "https://equalnotequals.com/achievements";

const ASCEND_DUR = 1.7; // seconds for the elevator to rise one floor

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

// ricochet-tier names for the score combo toast (index = bounce count)
const BOUNCE_NAMES = ["", "BANK SHOT", "DOUBLE BOUNCE", "TRIPLE BOUNCE", "QUAD BOUNCE"];

// the free-standing elevator car that carries the player up between floors
const CAR_GEOM = new THREE.BoxGeometry(38, 6, 38);
const CAR_MAT = new THREE.MeshLambertMaterial({ color: 0x39d98a });
function makeCar(): THREE.Mesh {
  const m = new THREE.Mesh(CAR_GEOM, CAR_MAT);
  m.castShadow = m.receiveShadow = true;
  return m;
}

// the bonus-upgrade pickup: a glowing rotating cube wrapped in an additive halo.
// Geometry + materials are shared module-level (every pickup looks the same) so
// respawning one each floor allocates nothing and can't leak GPU memory.
const PICKUP_CORE_GEOM = new THREE.BoxGeometry(11, 11, 11);
const PICKUP_HALO_GEOM = new THREE.SphereGeometry(11, 16, 12);
const PICKUP_CORE_MAT = new THREE.MeshLambertMaterial({ color: 0xffd23f, emissive: 0xffa800, emissiveIntensity: 0.9 });
const PICKUP_HALO_MAT = new THREE.MeshBasicMaterial({ color: 0xffe27a, transparent: true, opacity: 0.3, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
function makePickup(): THREE.Group {
  const g = new THREE.Group();
  const core = new THREE.Mesh(PICKUP_CORE_GEOM, PICKUP_CORE_MAT);
  core.castShadow = true;
  const halo = new THREE.Mesh(PICKUP_HALO_GEOM, PICKUP_HALO_MAT);
  g.add(core, halo);
  return g;
}

class Game {
  world: World;
  input: Input;
  hud: Hud;
  map = new GameMap();
  bullets!: Bullets;
  enemies!: EnemyManager;
  effects!: Effects;
  treads!: Treads;
  deathMarks!: DeathMarks;
  private deathMarkLayer = 0; // the layer the current death X's belong to — kept across a same-floor death-retry, wiped on a real floor change
  aimLine!: AimLine;
  player!: Tank;
  achievements = new AchievementSystem();

  // bonus-upgrade pickup (one per floor, tucked in a hard-to-reach spot)
  private pickupGroup: THREE.Group | null = null;
  private pickupPos = new THREE.Vector3();
  private pickupLevel = 0;

  private geo: Awaited<ReturnType<typeof loadTankGeometries>> | null = null;
  private guidebook: Guidebook | null = null;
  private cardPortraitTick = 0; // round-robin index for the live hand-card portraits
  private guideOpen = false;
  // CARD MODE: no player tank — deploy ally tanks from an evolving hand onto your half.
  private cardMode = false;
  private cardDeck: CardDeck | null = null;
  private cardFocus = new THREE.Vector3(); // free-pan camera target
  private cardSelected = -1; // hand index currently picked for placement (-1 = none)
  private readonly cardCell = { c: 0, r: 0, x: 0, z: 0, ok: false, hit: false }; // the hovered placement cell
  private cardWinTimer = 0; // delay between clearing a floor and auto-ascending
  private cardHighlight?: THREE.Mesh; // the green/red placement marker
  private cardPrevFire = false; // edge-detect the place click
  private readonly cardZeroVel = new THREE.Vector3();
  private session: atproto.Session | null = null;
  private profileStore: ProfileStore | null = null;
  private savedState: SavedGameState | null = null;
  private cardSavedState: SavedGameState | null = null;
  private booted = false;

  // dev/testing (toggle panel with `). devGod = infinite health. tainted = a dev
  // action was used this run → achievements + profile progress are disabled.
  private devGod = false;
  private tainted = false;
  private cardKillReward = CARD_MODE.killReward; // dev-tunable cash per kill (card-mode economy dial)
  private devCardUnlock = false; // dev: bypass the layer-25 card-mode unlock for testing

  mode: Mode = "menu";
  // title-screen attract demo: a lone mercenary fights floor 10 on a loop behind the menu
  private demoActive = false;
  private demoResetIn = 0; // countdown before re-racking the floor (after a win or a death)
  private demoExiting = false; // floor cleared — the merc is walking to the exit elevator
  private demoExitT = 0; // failsafe timer while walking to the exit (rides up anyway if it can't path there)
  private readonly demoExitPt = { x: 0, z: 0 }; // navigable staging point in front of the exit elevator (set when exiting begins)
  private demoPeakLayer = 10; // highest floor the demo merc has reached this climb (= DEMO_LAYER; reset in startDemo)
  private readonly demoVel = new THREE.Vector3();
  private readonly demoFocus = new THREE.Vector3();
  private phase: Phase = "combat";
  private layer = 1; // 1-based; the ground floor is layer 1
  private lives = LIVES_START;
  private maxLives = LIVES_START;
  private runKills = 0;
  private score = 0; // accumulated points this run (distance + bounce scoring)
  private floorY = 0;
  private arena: Arena | null = null; // current floor's procedural obstacle layout
  private transitioning = false; // mid elevator ride — sim frozen
  private confetti = new Confetti(); // celebration burst on achievements + layer wins (ported from zombies)
  private paused = false;
  private drafting = false; // between-floor upgrade draft is up — sim frozen
  private cameraYaw = 0;
  private cameraZoom = 1;
  private layerZoom = 1; // gentle camera pull-back on bigger (later) floors, on top of cameraZoom

  // elevator rise animation
  private ascending = false;
  private ascendT = 0;
  private ascendFromY = 0;
  private ascendToY = 0;
  // departing→arriving hole XZ (they differ at grid-growth boundaries); the rise
  // lerps between them so the player lands on the new hole with no teleport.
  private ascendFromX = 0;
  private ascendFromZ = 0;
  private ascendToX = 0;
  private ascendToZ = 0;
  private ascendFromIdx = 0;
  private ascendExitIsA = false;
  private elevatorCar: THREE.Mesh | null = null;

  // roguelike upgrades (the same system zombies uses)
  private up: UpgradeLevers = defaultLevers();
  private enemyUp: UpgradeLevers = defaultLevers(); // enemy team's accumulated per-floor perks
  private cardAllyUp: UpgradeLevers = defaultLevers(); // CARD MODE: the ally team's accumulated free per-floor perks
  private movingAlly = -1; // CARD MODE deploy phase: index of the survivor being repositioned (-1 = none)
  private upgradesTaken: Record<string, number> = {};
  // enemy-team perk pool — combat upgrades only, EXCLUDING punch (damage) and shields
  private static readonly ENEMY_PERKS = ["velocity", "rapidbore", "overload", "ricochet", "treads", "gyro"];
  private shields = 0; // 1-hit shields this floor (from Bulwark)

  // timing / clocks
  private now = 95; // start mid-morning so the title screen + first floor open in daylight (day/night cycles from here)
  private lastTime = performance.now();
  private fireCooldown = 0;
  private graceUntil = 0;

  // per-layer trackers (achievements)
  private layerStart = 0;
  private tookHitThisLayer = false;
  private noFireTimer = 0;
  private minesHitThisLayer = 0;
  private lastKillTime = -10;
  private wasOnElevator = false; // edge-trigger for the elevator ride
  private wasOnLift = false; // edge-trigger for the "press F" lift prompt
  private driving = false; // player tank is translating this frame (loops the tread-clatter sfx)
  private turretSpinning = false; // player turret rotated meaningfully this frame (loops the servo sfx)

  // scratch
  private prevPlayer = new THREE.Vector3();
  private playerVel = new THREE.Vector3();
  private aimPoint = new THREE.Vector3();
  private aimMuzzle = new THREE.Vector3();
  private aimFwd = new THREE.Vector3();
  private focus = new THREE.Vector3();

  private hudState: HudState = { layer: 1, lives: LIVES_START, maxLives: LIVES_START, shields: 0, tanksLeft: 0 };

  constructor(container: HTMLElement) {
    this.world = new World(container);
    this.input = new Input(this.world.domElement);
    this.hud = new Hud();

    this.hud.onLogin = (h) => this.doSignIn(h);
    this.hud.onPlay = () => this.startRun(false);
    this.hud.onResumeGame = () => this.startRun(true);
    this.hud.onRestart = () => this.startRun(false);
    this.hud.onMainMenu = () => this.toMenu();
    this.hud.onShowAchievements = () => this.openAchievements();
    this.hud.onAchClose = () => this.closeAchievements();
    this.hud.onShowGuide = () => this.openGuide();
    this.hud.onPlayCard = () => this.startCardRun();
    this.hud.onCardPick = (i) => this.pickCard(i);
    this.hud.onStartRound = () => this.startCardRound();
    this.hud.onLogout = () => void this.doLogout();
    this.hud.onResetData = () => void this.doResetData();
    this.hud.onSelectFlair = (v) => void this.selectFlair(v);
    this.hud.onVolume = (v) => {
      sfx.setVolume(v);
      shellChanged("volume", Math.round(v * 100));
      if (this.profileStore) { this.profileStore.profile.settings.volume = v; this.profileStore.save(); }
    };
    this.hud.onMusicVolume = (v) => { music.setVolume(v); shellChanged("music", Math.round(v * 100)); }; // music volume is local-only (zmusicvol), like sfx
    this.hud.getCurrentTrack = () => music.currentTrack();
    music.onNowPlaying = (t) => this.hud.musicCreditToast(t);
    startShell({
      // What this game lets the site change. Volumes are declared as whole numbers
      // 0..100; the shell syncs the one keyed `volume` to the account, and atproto
      // rejects any record containing a float.
      settings: () => [
        { key: "volume", label: "Sound", kind: "range", min: 0, max: 100, step: 1, value: Math.round(sfx.getVolume() * 100) },
        { key: "music", label: "Music", kind: "range", min: 0, max: 100, step: 1, value: Math.round(music.getVolume() * 100) },
      ],
      set: (k, v) => {
        if (k === "volume") sfx.setVolume(Number(v) / 100);
        else if (k === "music") music.setVolume(Number(v) / 100);
      },
    });   // no-op unless embedded

    this.achievements.onUnlock = (a) => {
      this.hud.achievementToast(a);
      this.confetti.burst(); // celebrate every unlock
      sfx.play("achieve");
      if (this.profileStore) {
        this.profileStore.profile.achievements = this.achievements.unlockedIds();
        this.profileStore.save();
      }
    };

    this.hud.onResume = () => this.resumeGame();

    // unlock audio on first interaction (sfx WebAudio + the music stream, same gesture)
    const unlock = () => { sfx.resume(); music.unlock(); };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("keydown", (e) => {
      if (e.code === "KeyM" && !e.repeat) { const m = sfx.toggleMute(); this.hud.flashBanner(m ? "MUTED" : "SOUND ON", 700); }
      // Embedded, Escape is the shell's: it opens the site's menu, and this
      // game must not put a second one on top of it.
      if (e.code === "Escape" && !e.repeat && !inShell) this.togglePause();
      if (e.code === "KeyF" && !e.repeat) this.tryRideLift();
    });

    new DevPanel({
      spawn: (id) => this.devSpawn(id),
      // at the menu, just set the flag (no taint/banner) — startRun taints from
      // frame 1 if god is still on; mid-run, enabling it taints immediately.
      setGod: (on) => { this.devGod = on; if (on && this.mode === "playing") this.taintRun(); },
      jump: (n) => this.devJumpLayer(n),
      clear: () => { if (this.mode === "playing") { this.taintRun(); this.enemies.clear(); } },
      getCashPerKill: () => this.cardKillReward,
      setCashPerKill: (n) => { this.cardKillReward = Math.max(0, Math.round(n)); if (this.mode === "playing" && this.cardMode) this.taintRun(); },
      getCardUnlock: () => this.cardModeUnlocked(),
      setCardUnlock: (on) => { this.devCardUnlock = on; if (this.mode === "menu") this.showReady(); }, // reveal the menu button now
    });

    void this.boot();
    void this.initAuth();
  }

  private async boot(): Promise<void> {
    this.hud.setStatus("loading…");
    this.geo = await loadTankGeometries();
    await this.map.load(this.world.scene);
    this.guidebook = new Guidebook(this.geo, this.map.groundColor()); // pause-menu field guide (live tank portraits on map-coloured ground)
    this.bullets = new Bullets(this.world.scene);
    this.effects = new Effects(this.world.scene);
    this.bullets.setEffects(this.effects);
    this.treads = new Treads(this.world.scene);
    this.deathMarks = new DeathMarks(this.world.scene);
    this.aimLine = new AimLine(this.world.scene, SHELL_COLOR.player);
    this.enemies = new EnemyManager(this.world.scene, this.geo, this.treads);
    this.enemies.setEffects(this.effects); // laser emitter muzzle-glow
    // a mercenary died (shell, mine, OR laser): gib it, and with Reinforcements redeploy it
    this.enemies.onMercDown = (tank) => {
      this.effects.gib(tank.pos.x, tank.floorY + 6, tank.pos.z, tank.floorY, 0, 0, tank.bodyColorHex());
      this.deathMarks.drop(tank.pos.x, tank.pos.z, tank.floorY); // a permanent white X where it fell
      if (this.cardMode) { sfx.play("explode", tank.pos); return; } // card mode: a deployed tank fell — just gib (no merc banner/redeploy)
      if (this.mode !== "playing") return; // the title-screen demo re-racks itself — no banner/redeploy
      sfx.play("mercdeath", tank.pos);
      if (this.mercRespawn && this.phase === "combat") { this.mercRespawnQueue.push(4.5); this.hud.flashBanner("MERCENARY DOWN — REDEPLOYING", 1300); }
      else this.hud.flashBanner("MERCENARY DOWN", 1100);
    };
    // kill feed (card mode only): every tank death, with its killer
    this.enemies.onTankKilled = (victim, killer) => { if (this.cardMode) this.hud.pushKill(killer, victim); };
    this.player = new Tank(this.geo, null, "You", true);
    this.player.parts.bodyMat.color.setHex(PLAYER_COLOR);
    this.player.parts.turretMat.color.setHex(PLAYER_COLOR);
    this.player.addToScene(this.world.scene);
    this.map.ensureLayers(0);
    this.booted = true;
    this.hud.setStatus("");
    this.startDemo(); // a mercenary fights floor 10 behind the title screen
    music.start(); // ambient music (defers until the first gesture; plays through menus + runs)
    log.info("boot", "ready");
    requestAnimationFrame(this.frame);
  }

  // ---- auth ----------------------------------------------------------

  private async initAuth(): Promise<void> {
    this.hud.setStatus("checking sign-in…");
    const session = await atproto.initAuth().catch(() => null);
    if (session) await this.onLoggedIn(session);
    else this.hud.showLogin();
    this.hud.setStatus("");
  }

  private async onLoggedIn(session: atproto.Session): Promise<void> {
    this.session = session;
    this.profileStore = new ProfileStore(session);
    this.profileStore.onSaveError = (m) => this.hud.flashBanner(m, 3500);
    const [profile, saved, cardSaved] = await Promise.all([
      this.profileStore.load(),
      loadGameState(session, "self").catch(() => null),
      loadGameState(session, "cardmode").catch(() => null),
    ]);
    this.savedState = saved;
    this.cardSavedState = cardSaved;
    this.achievements.setUnlocked(profile.achievements);
    sfx.setVolume(profile.settings.volume);
    this.hud.setVolumeValue(profile.settings.volume);
    this.hud.setMusicVolumeValue(music.getVolume()); // music volume is local-only (zmusicvol)
    this.showReady();
    if (!atproto.canWrite(session)) {
      this.hud.flashBanner("⚠ this session can't save — log out and sign in again", 3500);
    }
    log.info("auth", `signed in @${session.handle}${saved ? ` (saved layer ${saved.layer})` : ""}`);
  }

  private async doSignIn(handle: string): Promise<void> {
    if (!handle) { this.hud.showLogin("enter your handle (e.g. you.bsky.social)"); return; }
    this.hud.setStatus("redirecting to Bluesky…");
    try { await atproto.signIn(handle); }
    catch (e) { this.hud.showLogin(`sign-in failed: ${(e as Error)?.message ?? e}`); }
  }

  private async doLogout(): Promise<void> {
    if (this.session) await atproto.signOut(this.session);
    this.session = null;
    this.profileStore = null;
    this.savedState = null;
    this.achievements.setUnlocked([]);
    this.enemies.setMutualNames([]);
    this.hud.showLogin();
  }

  private async doResetData(): Promise<void> {
    if (this.profileStore) {
      this.profileStore.profile = emptyProfile();
      await this.profileStore.saveNow();
    }
    this.achievements.setUnlocked([]);
    if (this.session) await clearGameState(this.session).catch(() => {});
    this.savedState = null;
    this.hud.flashBanner("ALL DATA RESET", 1500);
    this.showReady();
  }

  /** Send the player to the shared picker.
   *
   *  This used to write `flair` into our own repo. It no longer does: the worn
   *  flair is ONE choice across every game, and storing it per game meant the
   *  labeler had to pick a winner by whichever record was written last — a race
   *  that let Zombies silently overwrite a choice made here. One writer now, at
   *  equalnotequals.com/achievements.
   *
   *  The value rides along as a hash so the picker can open on the achievement
   *  the player actually clicked. */
  private async selectFlair(value: string): Promise<void> {
    const url = value ? `${PICKER_URL}#tanks-${value}` : PICKER_URL;
    window.open(url, "_blank", "noopener");
  }

  // ---- screens -------------------------------------------------------

  private showReady(): void {
    const p = this.profileStore?.profile;
    this.hud.showReady(
      this.session?.handle ?? "",
      this.achievements.count, this.achievements.total,
      p?.highestLayer ?? 0,
      this.savedState?.layer ?? 0,
      this.cardSavedState?.layer ?? 0,
      this.cardModeUnlocked(),
    );
  }

  /** Card mode unlocks by reaching layer 25 in a normal climb ("Skill: Average"); dev can bypass. */
  private cardModeUnlocked(): boolean {
    return this.devCardUnlock || this.achievements.has("layer_25");
  }

  /** Leave card mode: drop its UI + restore the player tank for normal play/the demo. */
  private exitCardMode(): void {
    if (!this.cardMode) return;
    this.cardMode = false;
    this.cardSelected = -1;
    this.movingAlly = -1;
    this.hud.hideCardBar();
    this.hud.hideCashOdo();
    this.hud.hideStartRound();
    this.hud.clearKillFeed();
    if (this.cardHighlight) this.cardHighlight.visible = false;
    this.player.addToScene(this.world.scene); // was removed for card mode
  }

  private toMenu(): void {
    this.exitCardMode();
    this.mode = "menu";
    this.enemies.clear();
    this.enemies.clearAllies();
    this.bullets.clear();
    this.effects.clear();
    this.treads.clear();
    this.deathMarks.clear(); this.deathMarkLayer = 0;
    this.clearPickup();
    this.aimLine.hide();
    this.startDemo(); // resume the floor-10 attract demo behind the menu
    this.showReady();
  }

  private achScreenOpen = false;
  private openAchievements(justWore?: string): void {
    this.achScreenOpen = true;
    this.hud.showAchievements(new Set(this.achievements.unlockedIds()), this.profileStore?.profile.flair ?? "", justWore);
  }
  private closeAchievements(): void {
    this.achScreenOpen = false;
    if (this.mode === "playing" && this.paused) this.hud.showPaused();
    else if (this.mode === "playing") this.hud.hideOverlay();
    else this.showReady();
  }

  private openGuide(): void {
    if (!this.guidebook) return;
    this.guideOpen = true;
    this.hud.showGuide(this.guidebook.buildContent(), () => this.closeGuide());
  }
  private closeGuide(): void {
    this.guideOpen = false;
    this.guidebook?.stop(); // halt the live render loop + free its GL context
    this.hud.showPaused(); // the guide only opens from the pause menu
  }

  // ---- CARD MODE ------------------------------------------------------
  // A deckbuilder run on its own save slot: no player tank — you spend cash to deploy
  // ally tanks from an evolving hand onto your half of the grid, wipe each floor's enemy
  // roster, and climb. You lose only when all your tanks are gone and you can't field more.

  private startCardRun(): void {
    if (!this.booted) return;
    if (!this.cardModeUnlocked()) { this.hud.flashBanner("CARD MODE LOCKED — reach layer 25 in a climb", 2200); return; }
    this.stopDemo();
    sfx.resume();
    this.cardMode = true;
    this.runKills = 0;
    this.score = 0;
    this.layer = 1;
    this.tainted = this.devGod;
    this.paused = false;
    this.drafting = false;
    this.cardDeck = new CardDeck();
    this.cardSelected = -1;
    this.cardWinTimer = 0;
    this.enemyUp = defaultLevers();
    this.enemies.setEnemyUpgrades(this.enemyUp);
    this.cardAllyUp = defaultLevers();
    this.enemies.setAllyUpgrades(this.cardAllyUp); // card allies get their OWN levers (was inheriting stale normal/demo levers)
    const resume = !!this.cardSavedState;
    if (resume && this.cardSavedState) {
      this.layer = Math.max(1, this.cardSavedState.layer);
      this.runKills = Math.max(0, this.cardSavedState.kills | 0);
      this.score = Math.max(0, this.cardSavedState.score ?? 0);
      this.cardDeck.cash = this.cardSavedState.cash ?? CARD_MODE.startCash;
      if (this.cardSavedState.tainted) this.tainted = true;
      for (let i = 0; i < this.layer - 1; i++) this.rollEnemyPerk(false); // enemies accrue a perk per floor cleared
      for (let i = 0; i < this.layer - 1; i++) this.rollAllyPerk(false); // your team accrues a free perk per floor (parity)
    } else if (this.session) {
      void clearGameState(this.session, "cardmode");
      this.cardSavedState = null;
    }
    this.achievements.suspended = true; // card mode is its own thing — no climb achievements (v1)
    this.bullets.clear();
    this.effects.clear();
    this.clearPickup();
    this.enemies.clearAllies();
    this.player.removeFromScene(this.world.scene); // no player tank in card mode
    this.aimLine.hide();
    this.mode = "playing";
    this.hud.hideOverlay();
    this.hud.setScore(this.score);
    this.enterCardLayer();
    const handle = this.session?.handle;
    if (handle && !this.enemies.hasMutuals) void this.loadMutuals(handle); // mutuals are still the enemy team
  }

  /** Set up the current card-mode floor: geometry, enemy roster, free-pan camera. No player. */
  private enterCardLayer(): void {
    const idx = this.layer - 1;
    this.floorY = this.map.floorY(idx);
    this.map.extraAbove = 0;
    this.map.ensureLayers(idx);
    this.map.glowPlatform(idx, true, false);
    this.map.glowPlatform(idx, false, false);
    this.arena = this.map.arenaFor(idx) ?? null;
    setDynamicWalls(this.arena?.walls ?? []);
    setLayerBounds(idx);
    this.bullets.setArena(this.arena);
    this.layerZoom = layerLayout(idx).zoom;
    { const L = layerLayout(idx); this.world.layoutClouds(L.halfX, L.halfZ, this.floorY); } // world-fixed clouds, sized to this floor
    // park the (hidden) player at arena centre — used ONLY as the merc-AI rally + spawn-avoid anchor
    this.player.floorY = this.floorY; this.player.level = 0; this.player.riding = false; this.player.alive = true;
    this.player.pos.set(0, this.floorY, 0);
    this.treads.clear();
    this.hud.clearKillFeed();
    if (this.layer !== this.deathMarkLayer) { this.deathMarks.clear(); this.deathMarkLayer = this.layer; }
    // enemies spawn on THEIR half only (nearer the exit hole), then surface FROZEN for the deploy phase
    const entry = this.entryHole(this.layer), exit = this.exitHole(this.layer);
    this.enemies.spawnLayer(idx, this.floorY, this.arena, this.player.pos,
      { entryX: entry.cx, entryZ: entry.cz, exitX: exit.cx, exitZ: exit.cz });
    this.enemies.clearAllies();
    this.enemies.flushInitialWave(this.player.pos); // pop the opening wave up + freeze it so the player can plan
    this.phase = "deploy"; // setup phase: sim paused, place tanks, then START ROUND
    this.cardSelected = -1; this.movingAlly = -1;
    this.cardWinTimer = 0;
    this.layerStart = this.now;
    // free-pan camera, pulled back so you can read the battlefield
    this.cardFocus.set(0, this.floorY, 0);
    this.cameraZoom = Math.min(CAMERA.maxZoom, 2.2);
    this.world.snapTo(this.cardFocus, this.cameraYaw, this.cameraZoom * this.layerZoom);
    this.hud.flashBanner(`FLOOR ${this.layer} — DEPLOY`, 1600);
    this.hud.showStartRound();
    this.saveCardCheckpoint();
  }

  private cardAdvance(): void {
    // surviving CARD-DEPLOYED tanks climb with you (free minions don't — their summoner
    // re-spawns those). This eases the early-floor money crunch: your investment persists
    // instead of being rebuilt from a broke bank every floor.
    const survivors = this.enemies.allies
      .filter((a) => a.alive && a.cardLevel > 0)
      .map((a) => ({ typeId: a.type!.id, level: a.cardLevel, mecha: a.isMecha }));
    this.layer += 1;
    this.rollEnemyPerk(false); // the enemy team gets tougher each floor
    this.rollAllyPerk(); // YOUR team gains a random free perk each floor (banner) — rolled before deploy so new shields apply
    this.cardSelected = -1;
    this.enterCardLayer(); // clears allies + spawns the new roster (frozen, deploy phase); cash carries over
    this.redeploySurvivors(survivors); // seed the survivors on the new floor (player can then reposition them during deploy)
  }

  /** Re-deploy the tanks that survived the previous floor onto the new one, on valid cells
   *  nearest your entry. They return at full health (a fresh deployAlly at their card level). */
  private redeploySurvivors(survivors: { typeId: string; level: number; mecha: boolean }[]): void {
    const a = this.arena;
    if (!a || !survivors.length) return;
    const entry = this.entryHole(this.layer);
    const cells: { x: number; z: number; d: number }[] = [];
    for (let c = 0; c < a.cols; c++) for (let r = 0; r < a.rows; r++) {
      const x = a.cellCenterX(c), z = a.cellCenterZ(r);
      if (!this.validCardCell(x, z)) continue;
      cells.push({ x, z, d: (x - entry.cx) ** 2 + (z - entry.cz) ** 2 });
    }
    cells.sort((p, q) => p.d - q.d);
    survivors.forEach((s, i) => {
      const cell = cells[i] ?? cells[cells.length - 1];
      const x = cell ? cell.x : entry.cx, z = cell ? cell.z : entry.cz;
      this.enemies.deployAlly(s.typeId, s.level, x, z, this.floorY, s.mecha);
    });
  }

  private saveCardCheckpoint(): void {
    if (!this.session || this.tainted || !this.cardDeck) return;
    const state: SavedGameState = {
      savedAt: new Date().toISOString(),
      layer: this.layer, lives: 0, kills: this.runKills, score: this.score,
      cash: Math.floor(this.cardDeck.cash), tainted: this.tainted,
    };
    this.cardSavedState = state;
    void saveGameState(this.session, state, "cardmode");
  }

  private pickCard(i: number): void {
    if (!this.cardMode || !this.cardDeck) return;
    if (i < 0 || i >= this.cardDeck.slots.length || !this.cardDeck.slots[i]) { this.cardSelected = -1; return; } // stale/phantom slot → clear
    this.cardSelected = this.cardSelected === i ? -1 : i; // switch to card i; re-click the SAME card to deselect (the only cancel gesture)
  }

  /** Card-mode sim: evolve the hand, run the battle (allies vs mutual enemies), handle
   *  placement, and check win (floor cleared → ascend) / lose (no tanks + can't field). */
  private updateCard(dt: number): void {
    if (this.transitioning || !this.cardDeck) return;
    this.cardDeck.update(dt);
    // per-type field counts drive the deploy-cost escalation — only CARD-DEPLOYED allies
    // count (cardLevel>0); free Hatchery/Abomination minions (cardLevel 0) don't make
    // your cards pricier.
    const fielded: Record<string, number> = {};
    for (const a of this.enemies.allies) if (a.cardLevel > 0) fielded[a.type!.id] = (fielded[a.type!.id] ?? 0) + 1;
    this.cardDeck.fielded = fielded;

    this.updateCardPlacement();

    // DEPLOY phase: sim frozen while you place/reposition tanks; Enter/Space (or the button) begins combat
    if (this.phase === "deploy") {
      if (this.input.consumeKeyPress("Enter") || this.input.consumeKeyPress("Space")) this.startCardRound();
    }

    if (this.phase === "combat" || this.phase === "cleared") {
      const threats: Tank[] = [...this.enemies.allies]; // enemies hunt your tanks (no player)
      this.enemies.update(dt, this.player, this.cardZeroVel, this.bullets, this.arena, threats);
      this.enemies.updateAllies(dt, this.player, this.bullets, this.arena, true); // your tanks fight on team 0; hold where placed when no target
      const targets: Tank[] = [...this.enemies.allies, ...this.enemies.enemies];
      const events = this.bullets.update(dt, targets);
      for (const ev of events) {
        if (ev.shotDown || ev.absorbed || ev.leg || ev.tank.team === 0) continue; // ally deaths gib via onMercDown; leg hits aren't kills
        this.effects.gib(ev.tank.pos.x, ev.tank.floorY + 6, ev.tank.pos.z, ev.tank.floorY, ev.dirx, ev.dirz, ev.tank.bodyColorHex());
        this.deathMarks.drop(ev.tank.pos.x, ev.tank.pos.z, ev.tank.floorY);
        sfx.play("explode");
        this.runKills++;
        this.cardDeck.earn(this.cardKillReward);
        this.score += 25;
      }
    }
    this.effects.update(dt);
    this.treads.update();
    this.deathMarks.update();

    // win: floor wiped → auto-ascend after a short beat
    if (this.phase === "combat" && this.enemies.layerCleared) {
      this.phase = "cleared";
      this.cardWinTimer = 1.6;
      this.confetti.burst(); // layer win
      sfx.play("win");
      this.hud.flashBanner("FLOOR CLEARED ↑", 1500);
      this.score += 100 * this.layer;
      this.hud.addScore(this.score, 1, "");
    }
    if (this.phase === "cleared") { this.cardWinTimer -= dt; if (this.cardWinTimer <= 0) this.cardAdvance(); }

    // lose: all your tanks gone AND nothing in hand you can afford to field
    if (this.phase === "combat" && !this.enemies.layerCleared && this.enemies.allies.length === 0 && !this.cardDeck.anyAffordable()) {
      void this.gameOver();
    }
  }

  /** Hover a grid cell for the selected card / the survivor being moved; act on a click. */
  private updateCardPlacement(): void {
    const ndc = this.input.ndc();
    this.cardCell.hit = false;
    const wantCell = this.cardSelected >= 0 || (this.phase === "deploy" && this.movingAlly >= 0);
    if (wantCell && this.arena && this.world.screenToGround(ndc.x, ndc.y, this.floorY, this.aimPoint)) {
      const cell = this.arena.cellOf(this.aimPoint.x, this.aimPoint.z);
      const x = this.arena.cellCenterX(cell.c), z = this.arena.cellCenterZ(cell.r);
      this.cardCell.c = cell.c; this.cardCell.r = cell.r; this.cardCell.x = x; this.cardCell.z = z;
      this.cardCell.ok = this.validCardCell(x, z);
      this.cardCell.hit = true;
    }
    this.updateCardHighlight();
    // act on a left-click (rising edge)
    const fire = this.input.fireHeld;
    if (fire && !this.cardPrevFire) {
      if (this.cardSelected >= 0 && this.cardCell.hit && this.cardCell.ok && this.cardDeck) {
        // BUY + deploy a card
        const card = this.cardDeck.slots[this.cardSelected];
        if (card && this.cardDeck.canAfford(card)) {
          const bought = this.cardDeck.buy(this.cardSelected);
          if (bought) {
            this.enemies.deployAlly(bought.type, bought.level, this.cardCell.x, this.cardCell.z, this.floorY, !!bought.mecha);
            sfx.play("powerup");
            this.cardSelected = -1; // re-pick to place another
          }
        }
      } else if (this.phase === "deploy" && this.cardSelected < 0) {
        // DEPLOY: click a survivor to pick it up, then click a valid cell to drop it
        if (this.movingAlly < 0) {
          this.movingAlly = this.pickAllyUnderCursor();
          if (this.movingAlly >= 0) sfx.play("buy");
        } else if (this.cardCell.hit && this.cardCell.ok) {
          const a = this.enemies.allies[this.movingAlly];
          if (a) { a.pos.set(this.cardCell.x, this.floorY, this.cardCell.z); a.treadX = this.cardCell.x; a.treadZ = this.cardCell.z; a.sync(); }
          this.movingAlly = -1;
          sfx.play("powerup");
        }
      }
    }
    this.cardPrevFire = fire;
  }

  /** Nearest living ally tank under the cursor (for deploy-phase repositioning), or -1. */
  private pickAllyUnderCursor(): number {
    const ndc = this.input.ndc();
    if (!this.arena || !this.world.screenToGround(ndc.x, ndc.y, this.floorY, this.aimPoint)) return -1;
    let best = -1, bestD = 24 * 24;
    for (let i = 0; i < this.enemies.allies.length; i++) {
      const a = this.enemies.allies[i];
      if (!a.alive) continue;
      const d = (a.pos.x - this.aimPoint.x) ** 2 + (a.pos.z - this.aimPoint.z) ** 2;
      if (d < bestD) { bestD = d; best = i; }
    }
    return best;
  }

  /** Is this cell a legal deploy spot — reachable walkable ground, on YOUR half, unoccupied? */
  private validCardCell(x: number, z: number): boolean {
    const a = this.arena; if (!a) return false;
    if (!a.isReachable(x, z)) return false; // reachable ⇒ valid, non-solid, non-hole ground
    const entry = this.entryHole(this.layer), exit = this.exitHole(this.layer);
    if ((x - entry.cx) ** 2 + (z - entry.cz) ** 2 > (x - exit.cx) ** 2 + (z - exit.cz) ** 2) return false; // enemy half
    for (let i = 0; i < this.enemies.allies.length; i++) {
      if (i === this.movingAlly) continue; // the tank being repositioned doesn't block its own destination
      const t = this.enemies.allies[i];
      if ((t.pos.x - x) ** 2 + (t.pos.z - z) ** 2 < 26 * 26) return false;
    }
    for (const t of this.enemies.enemies) if ((t.pos.x - x) ** 2 + (t.pos.z - z) ** 2 < 26 * 26) return false;
    return true;
  }

  private updateCardHighlight(): void {
    if (!this.cardHighlight) {
      const geo = new THREE.PlaneGeometry(36, 36); geo.rotateX(-Math.PI / 2);
      const mat = new THREE.MeshBasicMaterial({ color: 0x39d98a, transparent: true, opacity: 0.42, depthWrite: false });
      this.cardHighlight = new THREE.Mesh(geo, mat);
      this.cardHighlight.renderOrder = 5;
      this.world.scene.add(this.cardHighlight);
    }
    const moving = this.phase === "deploy" && this.movingAlly >= 0;
    const show = (this.cardSelected >= 0 || moving) && this.cardCell.hit;
    this.cardHighlight.visible = show;
    if (show) {
      const col = !this.cardCell.ok ? 0xff4040 : moving ? 0x39c5ff : 0x39d98a; // cyan when repositioning a survivor, green when placing a card
      (this.cardHighlight.material as THREE.MeshBasicMaterial).color.setHex(col);
      this.cardHighlight.position.set(this.cardCell.x, this.floorY + 0.5, this.cardCell.z);
    }
  }

  private updateCardHud(): void {
    if (!this.cardDeck) return;
    this.hud.showCardBar(CARD_MODE.coreSlots);
    const deck = this.cardDeck;
    const cards = deck.slots.map((c) => {
      const t = TANK_TYPES[c.type];
      return {
        name: c.mecha ? "MECHA " + t.name : t.name, color: t.color, level: c.level, cost: deck.cost(c), afford: deck.canAfford(c),
        badge: (c.priceMult < 0.95 ? "off" : c.priceMult > 1.05 ? "up" : "neutral") as "off" | "neutral" | "up",
        warn: c.warn,
      };
    });
    this.hud.updateCardBar(deck.cash, cards, this.cardSelected);
    this.hud.updateCardTop(this.layer, this.enemies.onFieldCount + this.enemies.pendingCount, this.enemies.allies.length);
    // live 3D portraits on the hand cards: render ONE per frame (round-robin) through the
    // shared field-guide studio — tiny cost on top of the arena render. Skip while paused or
    // while the field guide itself is using that one offscreen renderer.
    if (this.guidebook && !this.paused && !this.guideOpen) {
      this.guidebook.advanceSpin();
      const i = this.cardPortraitTick++ % CARD_MODE.coreSlots;
      const cv = this.hud.cardCanvas(i);
      if (cv && deck.slots[i]) this.guidebook.renderPortrait(deck.slots[i].type, cv);
    }
  }

  // ---- pause ----------------------------------------------------------

  private togglePause(): void {
    if (this.mode !== "playing" || this.drafting || this.transitioning) return;
    // Esc over a sub-panel (achievements / field guide) steps back to the pause menu
    if (this.paused && this.guideOpen) { this.closeGuide(); return; }
    if (this.paused && this.achScreenOpen) { this.closeAchievements(); return; }
    this.paused = !this.paused;
    this.achScreenOpen = false;
    this.guideOpen = false;
    if (this.paused) { this.hud.showPaused(); this.saveCheckpoint(); }
    else this.hud.hideOverlay();
  }
  private resumeGame(): void {
    this.paused = false;
    this.achScreenOpen = false;
    this.guideOpen = false;
    this.guidebook?.stop(); // ensure the guide's render loop never runs into gameplay
    this.hud.hideOverlay();
  }

  // ---- round-end shop + upgrade draft (the roguelike system) ----------

  /** mercenaries allowed on the field at once + the (escalating) shop prices. */
  private static readonly ALLY_CAP = 2;
  private currentDraft: UpgradeDef[] = []; // the 3 perks on offer (a reroll replaces them)
  private alliesBought = 0; // mercs OWNED this run (capped at ALLY_CAP) — they respawn each floor, no rebuy
  private mercRespawn = false; // "reinforcements" upgrade bought — dead mercs also redeploy mid-floor
  private mercRespawnQueue: number[] = []; // countdown timers for pending mid-floor merc redeploys
  private rerollsThisDraft = 0; // resets each shop visit — drives the escalating reroll price
  private mercCost(): number { return 6000 + 3000 * this.alliesBought; }
  private mercRespawnCost(): number { return 10000; }
  private rerollCost(): number { return 700 + 500 * this.rerollsThisDraft; }
  /** Cost to BUY a perk: a base that climbs gently with depth (roughly tracking per-floor income),
   *  plus a LINEAR surcharge per copy of THIS perk already owned — so re-taking the same buff keeps
   *  costing more, but never runs away exponentially (by wave 50 a player has ~100 perks; the old
   *  1.7^n made deep stacks absurd). Stacking one perk hard is still the priciest path. */
  private perkCost(id: string): number {
    const taken = this.upgradesTaken[id] ?? 0;
    const depth = 1 + 0.07 * (this.layer - 1); // gentle, ~tracks income growth over the run
    const repeat = 1 + 0.4 * taken;            // linear: each copy of the SAME perk adds 40% of base
    return Math.round((400 * depth * repeat) / 10) * 10;
  }

  /** Open the round-end shop: pick a free perk, hire a mercenary, or reroll the perks. */
  private openShop(): void {
    if (this.mode !== "playing") return;
    this.drafting = true; // freezes the sim (same gate as the old draft)
    this.currentDraft = rollDraft(3);
    this.rerollsThisDraft = 0;
    this.refreshShop();
  }
  /** (Re)render the shop with the current points / prices / availability. */
  private refreshShop(): void {
    const cap = Game.ALLY_CAP;
    this.hud.showShop({
      layer: this.layer,
      choices: this.currentDraft,
      tally: { ...this.upgradesTaken },
      points: this.score,
      mercCost: this.mercCost(),
      mercSoldOut: this.alliesBought >= cap,
      mercAffordable: this.score >= this.mercCost() && this.alliesBought < cap,
      allyCount: this.alliesBought, // mercs OWNED (they come back each floor) — not the living count
      allyCap: cap,
      respawnCost: this.mercRespawnCost(),
      respawnOwned: this.mercRespawn,
      respawnAffordable: this.score >= this.mercRespawnCost() && !this.mercRespawn,
      rerollCost: this.rerollCost(),
      rerollAffordable: this.score >= this.rerollCost(),
      perkCost: (id) => this.perkCost(id),
      onPick: (id) => this.applyDraftPick(id),
      onBuyMerc: () => this.buyMercenary(),
      onBuyRespawn: () => this.buyMercRespawn(),
      onReroll: () => this.rerollPerks(),
      onSkip: () => this.closeShop(),
    });
  }
  private buyMercenary(): void {
    const cost = this.mercCost();
    if (this.alliesBought >= Game.ALLY_CAP || this.score < cost) { sfx.play("deny"); return; }
    this.score -= cost;
    this.alliesBought += 1;
    this.hud.setScore(this.score);
    if (this.enemies.allyCount < Game.ALLY_CAP) this.enemies.spawnAlly(this.player.pos, this.floorY); // join this floor now
    sfx.play("powerup");
    this.saveCheckpoint();
    this.refreshShop();
  }
  /** "Reinforcements" upgrade: a one-time buy so dead mercs redeploy mid-floor (not just next floor). */
  private buyMercRespawn(): void {
    const cost = this.mercRespawnCost();
    if (this.mercRespawn || this.score < cost) { sfx.play("deny"); return; }
    this.score -= cost;
    this.mercRespawn = true;
    this.hud.setScore(this.score);
    sfx.play("powerup");
    this.saveCheckpoint();
    this.refreshShop();
  }
  /** Count down pending mid-floor merc redeploys (the "reinforcements" upgrade). */
  private tickMercRespawn(dt: number): void {
    if (this.phase !== "combat" || !this.mercRespawnQueue.length) return;
    const cap = Math.min(this.alliesBought, Game.ALLY_CAP);
    for (let i = this.mercRespawnQueue.length - 1; i >= 0; i--) {
      this.mercRespawnQueue[i] -= dt;
      if (this.mercRespawnQueue[i] > 0) continue;
      this.mercRespawnQueue.splice(i, 1);
      if (this.enemies.allyCount < cap) {
        this.enemies.spawnAlly(this.player.pos, this.floorY, false); // redeploy near the player
        sfx.play("mercrespawn", this.player.pos);
        this.hud.flashBanner("MERCENARY REDEPLOYED", 1000);
      }
    }
  }
  private rerollPerks(): void {
    const cost = this.rerollCost();
    if (this.score < cost) { sfx.play("deny"); return; }
    this.score -= cost;
    this.rerollsThisDraft += 1;
    this.hud.setScore(this.score);
    this.currentDraft = rollDraft(3);
    sfx.play("buy");
    this.refreshShop();
  }
  /** Each floor the enemy team gains one random combat perk (never Punch or shields). */
  private rollEnemyPerk(announce = true): void {
    const id = Game.ENEMY_PERKS[(Math.random() * Game.ENEMY_PERKS.length) | 0];
    applyUpgrade(id, { up: this.enemyUp, addLife: () => {} });
    if (announce) this.hud.flashBanner(`ENEMIES GAIN: ${UPGRADES[id].name.toUpperCase()}`, 1700);
  }
  /** CARD MODE: each floor YOUR team gains one random FREE perk (full pool — Punch/Bulwark/etc.). */
  private rollAllyPerk(announce = true): void {
    let def = rollDraft(1)[0];
    if (def.id === "spareparts") def = UPGRADES["punch"]; // no shared life pool in card mode → swap for a real buff
    applyUpgrade(def.id, { up: this.cardAllyUp, addLife: () => {} });
    if (announce) this.hud.flashBanner(`TEAM GAINS: ${def.name.toUpperCase()} — ${def.desc}`, 2000);
  }
  /** CARD MODE: begin the round — release the frozen enemy wave and run combat. */
  private startCardRound(): void {
    if (!this.cardMode || this.phase !== "deploy") return;
    this.phase = "combat";
    this.enemies.releaseInitialWave();
    for (const a of this.enemies.allies) a.riseTime = 0; // tanks placed during setup are ready — no rise hiccup
    this.cardSelected = -1; this.movingAlly = -1;
    this.hud.hideStartRound();
    this.layerStart = this.now;
    sfx.play("buy");
    this.hud.flashBanner("FIGHT!", 1000);
  }
  /** BUY one perk with points — that's your single perk for this clear, so it starts the floor. */
  private applyDraftPick(id: string): void {
    const cost = this.perkCost(id);
    if (this.score < cost) { sfx.play("deny"); return; }
    this.score -= cost;
    applyUpgrade(id, this.upgradeContext());
    this.upgradesTaken[id] = (this.upgradesTaken[id] ?? 0) + 1;
    this.hud.setScore(this.score);
    this.hud.setDraftPips(this.upgradesTaken); // keep the pip row in sync
    const def = UPGRADES[id];
    if (def) this.hud.flashBanner(`${def.name.toUpperCase()} — ${def.desc}`, 1500);
    sfx.play("powerup");
    this.closeShop(); // one perk per clear → entering the floor
  }
  /** Leave the round-end shop and start the (already-ascended-to) floor. */
  private closeShop(): void {
    this.drafting = false;
    this.hud.hideShop();
    this.shields = this.up.shieldHits; // grant the floor's shields (incl. a Bulwark just bought)
    this.saveCheckpoint();
  }
  private upgradeContext(): UpgradeContext {
    return {
      up: this.up,
      addLife: () => { this.maxLives += 1; this.lives += 1; },
    };
  }

  // ---- run / layer lifecycle ----------------------------------------

  private startRun(resume: boolean): void {
    if (!this.booted) return;
    this.stopDemo(); // tear down the title-screen demo before the real run
    sfx.resume();
    this.runKills = 0;
    this.score = 0;
    this.lives = LIVES_START;
    this.maxLives = LIVES_START;
    this.layer = 1;
    this.tainted = this.devGod; // god mode on at the start taints the run from frame 1
    this.paused = false;
    this.drafting = false;
    this.up = defaultLevers();
    this.enemyUp = defaultLevers();
    this.enemies.setAllyUpgrades(this.up); // mercenaries share the player's levers (mutated in place by picks)
    this.enemies.setEnemyUpgrades(this.enemyUp);
    this.upgradesTaken = {};
    this.hud.clearDrafts();
    if (resume && this.savedState) {
      this.layer = Math.max(1, this.savedState.layer);
      this.runKills = Math.max(0, this.savedState.kills | 0);
      this.score = Math.max(0, this.savedState.score ?? 0);
      // replay upgrade picks to rebuild the levers; addLife bumps the MAX only
      // (lives come from the save, which already reflects the bonus)
      for (const [id, n] of Object.entries(this.savedState.upgrades ?? {})) {
        for (let i = 0; i < (n | 0); i++) {
          if (applyUpgrade(id, { up: this.up, addLife: () => { this.maxLives += 1; } })) {
            this.upgradesTaken[id] = (this.upgradesTaken[id] ?? 0) + 1;
          }
        }
      }
      this.hud.setDraftPips(this.upgradesTaken);
      this.lives = Math.max(1, Math.min(this.savedState.lives, this.maxLives));
      // enemies accrue one perk per floor cleared; reseed (random, count-matched) on resume
      for (let i = 0; i < this.layer - 1; i++) this.rollEnemyPerk(false);
      if (this.savedState.tainted) this.tainted = true; // a dev-tainted save stays tainted
    } else if (this.session) {
      void clearGameState(this.session);
      this.savedState = null;
    }
    this.achievements.suspended = this.tainted;
    this.bullets.clear();
    this.effects.clear();
    this.clearPickup();
    this.enemies.clearAllies(); // a fresh run starts with no mercenaries
    this.alliesBought = (resume && this.savedState?.alliesBought) || 0;
    this.mercRespawn = (resume && this.savedState?.mercRespawn) || false;
    this.mercRespawnQueue.length = 0;
    this.mode = "playing";
    this.hud.hideOverlay();
    this.hud.setScore(this.score);
    this.enterLayer();
    // if the resumed floor's bonus pickup was already collected, don't re-offer it
    if (resume && this.savedState?.pickupTaken) { this.arena?.collectPickup(); this.clearPickup(); }
    // (owned mercenaries are re-deployed by enterLayer from alliesBought — no separate resume step)
    // if the round-end shop was open when saved, re-offer it (the merc is already restored)
    if (resume && this.savedState?.shopPending) this.openShop();
    const handle = this.session?.handle;
    if (handle && !this.enemies.hasMutuals) void this.loadMutuals(handle);
  }

  // The two holes alternate roles each layer: the player ENTERS at one and the
  // elevator (exit) is the other. Riding the elevator rises straight up to the
  // same XZ on the next floor — which is that floor's ENTRY hole — so the spawn
  // side flips every layer. (layer 1 enters at hole A / SPAWN_HOLE.)
  private entryIsA(layer: number): boolean { return layer % 2 === 1; }
  // world hole CENTRES for a layer (the floor grows per-layer, so these scale).
  private entryHole(layer: number) { const L = layerLayout(layer - 1); return this.entryIsA(layer) ? L.spawn : L.elev; }
  private exitHole(layer: number) { const L = layerLayout(layer - 1); return this.entryIsA(layer) ? L.elev : L.spawn; }

  /** Set up the current `this.layer`. `viaElevator` = the player just rode up and
   *  is already at the right position (don't reposition or snap the camera). */
  private enterLayer(viaElevator = false): void {
    const idx = this.layer - 1;
    this.floorY = this.map.floorY(idx);
    this.map.extraAbove = 0;
    this.map.ensureLayers(idx);
    this.map.glowPlatform(idx, true, false);
    this.map.glowPlatform(idx, false, false);
    this.arena = this.map.arenaFor(idx) ?? null;
    setDynamicWalls(this.arena?.walls ?? []); // solid blocks join tank collision + shell bounce (world coords)
    setLayerBounds(idx); // refresh this floor's world walls/interior/holes before any physics
    this.bullets.setArena(this.arena); // level-aware shell bounce

    this.layerZoom = layerLayout(idx).zoom; // mild camera pull-back on bigger floors (tile size stays constant)
    { const L = layerLayout(idx); this.world.layoutClouds(L.halfX, L.halfZ, this.floorY); } // world-fixed clouds, sized to this floor
    this.player.floorY = this.floorY;
    this.player.level = 0; // every floor starts on the ground
    this.player.riding = false;
    this.player.alive = true;
    this.player.health = 1;
    const at = this.entryHole(this.layer);
    if (!viaElevator) {
      this.player.pos.set(at.cx, this.floorY, at.cz);
    } else {
      // arrived by elevator — align XZ to this floor's entry hole. No-op unless this
      // floor changed size (then it corrects the small hole-position offset).
      this.player.pos.x = at.cx; this.player.pos.z = at.cz;
    }
    this.player.bodyYaw = yawToward(-this.player.pos.x, -this.player.pos.z); // face arena center
    this.player.turretYaw = this.player.bodyYaw;
    this.player.sync();
    this.treads.clear(); // fresh floor — wipe the previous floor's tread marks
    // death X's persist across a same-floor death-retry (same maze) but clear on a real floor change
    if (this.layer !== this.deathMarkLayer) { this.deathMarks.clear(); this.deathMarkLayer = this.layer; }
    this.player.treadX = this.player.pos.x; this.player.treadZ = this.player.pos.z;
    this.aimLine.hide(); // avoid a 1-frame stale line at the old position on respawn
    this.wasOnElevator = false;
    this.shields = this.up.shieldHits; // fresh shields each floor (Bulwark)

    this.enemies.spawnLayer(idx, this.floorY, this.arena, this.player.pos);
    // every floor (and on a life-loss restart) re-deploy ALL owned mercenaries fresh, in
    // front of the elevator — once hired they keep coming back, no need to rebuy the dead.
    this.enemies.clearAllies();
    this.mercRespawnQueue.length = 0;
    const mercs = Math.min(this.alliesBought, Game.ALLY_CAP);
    for (let i = 0; i < mercs; i++) this.enemies.spawnAlly(this.player.pos, this.floorY);
    this.spawnPickup();

    this.phase = "combat";
    this.graceUntil = this.now + 2.0;
    this.layerStart = this.now;
    this.tookHitThisLayer = false;
    this.noFireTimer = 0;
    this.minesHitThisLayer = 0;

    if (!viaElevator) {
      this.world.snapTo(this.focus.set(this.player.pos.x, this.floorY, this.player.pos.z), this.cameraYaw, this.cameraZoom * this.layerZoom);
    }
    this.hud.flashBanner(`LAYER ${this.layer}`, 1400);
    for (const m of [5, 10, 15, 20, 25, 30, 40, 50, 100]) {
      if (this.layer >= m) this.achievements.unlock(`layer_${m}`);
    }
    if (this.profileStore && !this.tainted) {
      this.profileStore.profile.highestLayer = Math.max(this.profileStore.profile.highestLayer, this.layer);
      this.profileStore.save();
    }
  }

  // ---- title-screen attract demo (a mercenary solos floor 10 on a loop) ------
  private static readonly DEMO_LAYER = 10;

  /** Begin the menu attract demo: an AI mercenary fights floor 10 on repeat. */
  private startDemo(): void {
    if (!this.booted || this.mode !== "menu") return;
    this.demoActive = true;
    sfx.silent = true; // the attract loop is a silent backdrop — no gunfire behind the menu
    // base combat stats, but a stack of shields so the attract-demo merc stays durable
    // enough to fight + climb (real-run mercs now match the player's shields, which would
    // leave a base-stats demo merc on 1 HP).
    const demoLevers = defaultLevers();
    demoLevers.shieldHits = 9;
    this.enemies.setAllyUpgrades(demoLevers);
    this.enemies.setEnemyUpgrades(null);
    this.layer = Game.DEMO_LAYER; // the climb starts at floor 10
    this.demoPeakLayer = Game.DEMO_LAYER;
    this.setupDemoFloor();
  }

  /** Tear the demo down (a real run is starting). */
  private stopDemo(): void {
    if (!this.demoActive) return;
    this.demoActive = false;
    sfx.silent = false;
    this.demoResetIn = 0;
    this.enemies.clear();
    this.enemies.clearAllies();
    this.bullets.clear();
    this.effects.clear();
    this.treads.clear();
    this.deathMarks.clear(); this.deathMarkLayer = 0;
  }

  /** Rack the current demo floor (`this.layer`): build its geometry, spawn the roster
   *  + one mercenary. The climb starts at floor 10 and advances as the merc clears. */
  private setupDemoFloor(): void {
    const idx = this.layer - 1;
    this.floorY = this.map.floorY(idx);
    this.map.extraAbove = 0;
    this.map.ensureLayers(idx);
    this.map.glowPlatform(idx, true, false);
    this.map.glowPlatform(idx, false, false);
    this.arena = this.map.arenaFor(idx) ?? null;
    setDynamicWalls(this.arena?.walls ?? []);
    setLayerBounds(idx);
    this.bullets.setArena(this.arena);
    this.layerZoom = layerLayout(idx).zoom;
    { const L = layerLayout(idx); this.world.layoutClouds(L.halfX, L.halfZ, this.floorY); } // world-fixed clouds, sized to this floor
    this.enemies.clearAllies();
    this.bullets.clear();
    this.effects.clear();
    this.treads.clear();
    this.deathMarks.clear(); // each demo floor starts clean
    // the mercenary rises out of the entry hole; the enemy roster fills the floor
    const entry = this.entryHole(this.layer);
    this.demoFocus.set(entry.cx, this.floorY, entry.cz);
    this.enemies.spawnLayer(idx, this.floorY, this.arena, this.demoFocus);
    this.enemies.spawnAlly(this.demoFocus, this.floorY); // spawn the solo merc IN FRONT OF the elevator (front=true, like a real run)
    this.world.snapTo(this.demoFocus, this.cameraYaw, this.cameraZoom * this.layerZoom);
    this.demoResetIn = 0;
    this.demoExiting = false;
    this.demoExitT = 0;
  }

  /** Drive the demo each menu frame: the merc fights the floor, then RIDES THE EXIT
   *  ELEVATOR up and PROGRESSES to the next floor — climbing until it finally falls,
   *  at which point the climb restarts from floor 10. (Floors don't reset on a win.) */
  private updateDemo(dt: number): void {
    if (!this.arena) return;
    const merc = this.enemies.allies[0] ?? null;

    if (this.demoResetIn > 0) {
      // brief pause to show the death, then restart the climb from floor 10
      this.demoResetIn -= dt;
      if (this.demoResetIn <= 0) {
        this.layer = Game.DEMO_LAYER;
        this.demoPeakLayer = Game.DEMO_LAYER;
        this.setupDemoFloor();
      }
    } else if (merc && merc.riding) {
      // riding the exit elevator up → advance to the next floor when the rise finishes
      this.bullets.update(dt, []); // let any in-flight shells expire mid-ride
      if (merc.advanceRide(dt / SIM_SPEED)) {
        this.layer += 1;
        this.demoPeakLayer = Math.max(this.demoPeakLayer, this.layer);
        this.setupDemoFloor();
      }
    } else if (this.demoExiting && merc) {
      // floor cleared — walk the merc to the staging point in front of the exit
      // elevator (guaranteed navigable+reachable), then step on and ride up
      this.bullets.update(dt, []);
      const fx = this.demoExitPt.x, fz = this.demoExitPt.z;
      this.enemies.driveAlliesTo(dt, fx, fz, 0, this.arena);
      this.demoExitT += dt;
      const nearFront = (merc.pos.x - fx) ** 2 + (merc.pos.z - fz) ** 2 < 55 * 55;
      if ((nearFront && merc.level === 0) || this.demoExitT > 14) {
        // step onto the elevator and ride up the shaft (advanceRide pulls it onto the hole)
        const exit = this.exitHole(this.layer);
        merc.startRide(0, merc.floorY, this.floorY + 240, exit.cx, exit.cz);
        merc.sync();
        this.demoExiting = false;
      }
    } else {
      // normal fighting: the merc is the stand-in "human" (spawn reference) AND the
      // only threat, so every enemy hunts it exactly as it would hunt the player.
      const threats: Tank[] = merc ? [merc] : [];
      const ref = merc ?? this.player;
      this.enemies.update(dt, ref, this.demoVel, this.bullets, this.arena, threats);
      this.enemies.updateAllies(dt, ref, this.bullets, this.arena);
      const targets: Tank[] = merc ? [merc, ...this.enemies.enemies] : [...this.enemies.enemies];
      for (const ev of this.bullets.update(dt, targets)) {
        if (ev.shotDown || ev.absorbed || ev.tank.team === 0) continue; // mercs are gibbed once by onMercDown on cull
        this.effects.gib(ev.tank.pos.x, ev.tank.floorY + 6, ev.tank.pos.z, ev.tank.floorY, ev.dirx, ev.dirz, ev.tank.bodyColorHex());
        this.deathMarks.drop(ev.tank.pos.x, ev.tank.pos.z, ev.tank.floorY);
        sfx.play("explode", ev.tank.pos);
      }
      if (this.enemies.allyCount === 0) this.demoResetIn = 2.4; // the merc fell — pause, then restart the climb
      else if (this.enemies.layerCleared) {
        // cleared alive → walk to the elevator. Resolve the (navigable, reachable)
        // staging point ONCE now, in front of this floor's exit hole.
        const exit = this.exitHole(this.layer);
        this.enemies.approachPoint(exit.cx, exit.cz, this.demoExitPt);
        this.demoExiting = true;
        this.demoExitT = 0;
      }
    }

    // re-read the merc (a re-rack above may have disposed the old one) before the camera track
    const m = this.enemies.allies[0] ?? null;
    if (m && m.alive) this.demoFocus.set(m.pos.x, m.floorY, m.pos.z);
    this.world.follow(this.demoFocus, dt, this.cameraYaw, this.cameraZoom * this.layerZoom);
    this.effects.update(dt);
    this.treads.update();
    this.deathMarks.update();
  }

  private onLayerCleared(): void {
    this.phase = "cleared";
    this.map.glowPlatform(this.layer - 1, !this.entryIsA(this.layer), true); // glow the EXIT hole
    this.confetti.burst(); // layer win
    sfx.play("win");
    this.hud.flashBanner("CLEAR — RIDE THE ELEVATOR ↑", 2200);
    this.score += 100 * this.layer; // floor-clear bonus (scales with depth)
    this.hud.addScore(this.score, 1, "FLOOR CLEAR");
    // per-layer achievements
    if (!this.tookHitThisLayer) this.achievements.unlock("flawless");
    if (this.now - this.layerStart < 15) this.achievements.unlock("speedrun");
    if (this.enemies.minesLaidThisLayer > 0 && this.minesHitThisLayer === 0) this.achievements.unlock("no_mine");
    this.saveCheckpoint();
  }

  /** Persist the run so it can be resumed (pause, floor-clear, draft, tab-hidden). */
  private saveCheckpoint(): void {
    if (this.cardMode) { this.saveCardCheckpoint(); return; } // card mode → its own slot
    if (!this.session || this.mode !== "playing") return;
    const state: SavedGameState = {
      savedAt: new Date().toISOString(),
      layer: this.layer, lives: this.lives, kills: this.runKills, score: this.score, tainted: this.tainted,
      upgrades: { ...this.upgradesTaken },
      pickupTaken: this.arena?.isPickupTaken() ?? false,
      allies: this.enemies.allies.filter((a) => a.alive).length,
      alliesBought: this.alliesBought,
      mercRespawn: this.mercRespawn,
      shopPending: this.drafting, // the round-end shop is open + unresolved — re-offer it on resume
    };
    this.savedState = state;
    void saveGameState(this.session, state);
  }

  // ---- bonus upgrade pickup ------------------------------------------

  /** (Re)spawn the floor's hidden bonus pickup from the arena's chosen spot. */
  private spawnPickup(): void {
    if (this.pickupGroup) { this.world.scene.remove(this.pickupGroup); this.pickupGroup = null; }
    const spot = this.arena?.pickupSpot();
    if (!spot) return;
    const g = makePickup();
    this.pickupLevel = spot.level;
    const y = this.floorY + (this.arena?.levelHeight(spot.level) ?? 0) + 14;
    this.pickupPos.set(spot.x, y, spot.z);
    g.position.copy(this.pickupPos);
    this.world.scene.add(g);
    this.pickupGroup = g;
  }

  private clearPickup(): void {
    if (this.pickupGroup) { this.world.scene.remove(this.pickupGroup); this.pickupGroup = null; }
  }

  /** Spin/bob the pickup; grant a random upgrade when the player reaches it (must
   *  be on the pickup's level — that's what makes a high-deck pickup hard to get). */
  private updatePickup(dt: number): void {
    const g = this.pickupGroup;
    if (!g) return;
    g.rotation.y += dt * 1.8;
    g.position.y = this.pickupPos.y + Math.sin(this.now * 2.5) * 3;
    if (this.player.riding || this.player.level !== this.pickupLevel) return;
    const dx = this.player.pos.x - this.pickupPos.x, dz = this.player.pos.z - this.pickupPos.z;
    if (dx * dx + dz * dz <= 17 * 17) {
      this.clearPickup();
      this.arena?.collectPickup();
      this.grantPickupUpgrade();
      sfx.play("powerup");
    }
  }

  /** Grant ONE random upgrade immediately (reuses the draft system; banner+pips). */
  private grantPickupUpgrade(): void {
    const choice = rollDraft(1)[0];
    if (!choice) return;
    applyUpgrade(choice.id, this.upgradeContext());
    this.upgradesTaken[choice.id] = (this.upgradesTaken[choice.id] ?? 0) + 1;
    this.shields = this.up.shieldHits;
    this.hud.setDraftPips(this.upgradesTaken);
    this.hud.flashBanner(`BONUS! ${choice.name.toUpperCase()} — ${choice.desc}`, 2200);
    this.score += 200 * this.layer; // reaching the (hard-to-get) pickup is worth a chunky bonus
    this.hud.addScore(this.score, 1, "PICKUP");
    this.saveCheckpoint();
  }

  /** Press F on an intra-floor lift → ride the road network up/down one (or more)
   *  levels. Carries the player smoothly between the ground and an overpass. */
  private tryRideLift(): void {
    if (this.mode !== "playing" || this.cardMode || this.paused || this.drafting || this.transitioning) return;
    if (this.player.riding || !this.arena) return;
    const lift = this.arena.liftAt(this.player.pos.x, this.player.pos.z, this.player.level);
    if (!lift) return;
    const center = { x: 0, z: 0 };
    this.arena.cellCenter(this.player.pos.x, this.player.pos.z, center);
    const toY = this.floorY + this.arena.levelHeight(lift.toLevel);
    this.player.startRide(lift.toLevel, this.player.floorY, toY, center.x, center.z);
    sfx.play("elevator");
  }

  /** Drive onto the active elevator → the car rises straight up through the hole
   *  in the floor directly above, carrying the player to the next layer (where
   *  the exit hole IS the entry hole — so the spawn side flips). */
  private rideElevator(): void {
    if (this.transitioning) return;
    this.transitioning = true;
    this.bullets.clear();
    this.enemies.clear();
    this.effects.clear();
    this.clearPickup();
    this.aimLine.hide();
    sfx.play("elevator");
    this.hud.flashBanner("GOING UP ↑", 1200);
    const fromIdx = this.layer - 1;
    this.ascendFromIdx = fromIdx;
    this.ascendFromY = this.map.floorY(fromIdx);
    this.ascendToY = this.map.floorY(fromIdx + 1);
    this.ascendExitIsA = !this.entryIsA(this.layer);
    // hide the layer's own exit platform — a dedicated car carries the player up
    // (so it isn't occluded by the opaque floor of the layer above)
    this.map.hidePlatform(fromIdx, this.ascendExitIsA, true);
    const L = layerLayout(fromIdx); // departing floor's per-axis size
    const ex = this.exitHole(this.layer); // departing exit-hole centre (player is here)
    const to = this.entryHole(this.layer + 1); // arriving entry-hole centre (player lands here)
    this.ascendFromX = ex.cx; this.ascendFromZ = ex.cz;
    this.ascendToX = to.cx; this.ascendToZ = to.cz;
    this.player.pos.set(ex.cx, this.floorY, ex.cz);
    this.player.sync();
    // a free-standing elevator car that rises with the player and lands flush
    this.elevatorCar?.removeFromParent();
    this.elevatorCar = makeCar();
    this.elevatorCar.scale.set(L.fx, 1, L.fz); // match the scaled hole it covers
    this.elevatorCar.position.set(ex.cx, this.ascendFromY - 3, ex.cz);
    this.world.scene.add(this.elevatorCar);
    this.ascendT = 0;
    this.ascending = true;
  }

  /** One frame of the elevator rise (runs while transitioning, off the live gate). */
  private updateAscend(dt: number): void {
    this.ascendT = Math.min(1, this.ascendT + dt / ASCEND_DUR);
    const e = easeInOut(this.ascendT);
    const y = this.ascendFromY + (this.ascendToY - this.ascendFromY) * e;
    this.player.floorY = y;
    // drift XZ from the departing hole to the arriving hole (only differs at a
    // grid-growth boundary) so the landing in enterLayer is a no-op — no teleport.
    this.player.pos.x = this.ascendFromX + (this.ascendToX - this.ascendFromX) * e;
    this.player.pos.z = this.ascendFromZ + (this.ascendToZ - this.ascendFromZ) * e;
    this.player.sync();
    if (this.elevatorCar) this.elevatorCar.position.set(this.player.pos.x, y - 3, this.player.pos.z); // car tracks the player
    if (this.ascendT >= 1) this.finishAscend();
  }

  private finishAscend(): void {
    this.ascending = false;
    this.map.glowPlatform(this.ascendFromIdx, this.ascendExitIsA, false);
    this.map.hidePlatform(this.ascendFromIdx, this.ascendExitIsA, false); // restore the old platform
    this.elevatorCar?.removeFromParent();
    this.elevatorCar = null;
    this.layer += 1;
    this.rollEnemyPerk(); // the enemy team gains one random perk on the new floor (mirrors the player's pick)
    this.enterLayer(true); // viaElevator: already at the right XZ/Y, camera in place
    this.transitioning = false;
    this.openShop(); // round-end shop: buy ONE perk, hire a mercenary, reroll, then START
  }

  // ---- dev / testing -------------------------------------------------

  /** Mark the run as dev-tainted: no achievements or profile progress from here. */
  private taintRun(): void {
    if (this.tainted) return;
    this.tainted = true;
    this.achievements.suspended = true;
    this.hud.flashBanner("DEV MODE — no achievements or progress this run", 2600);
    log.info("dev", "run tainted (dev tools used)");
  }

  /** Spawn one enemy of `id` via the normal spawn pipeline (a valid spot on the floor,
   *  clear of the player) — dev. */
  private devSpawn(id: string): void {
    if (this.mode !== "playing" || !this.arena) return;
    const type = TANK_TYPES[id];
    if (!type) return;
    this.taintRun();
    this.enemies.devSpawn(type, this.player.pos);
  }

  /** Jump straight to layer `n` (fresh) — dev. */
  private devJumpLayer(n: number): void {
    if (this.mode !== "playing" || this.transitioning) return;
    this.taintRun();
    this.layer = Math.max(1, Math.floor(n));
    this.bullets.clear();
    this.enemies.clear();
    this.effects.clear();
    this.enterLayer();
  }

  private loseLife(): void {
    // dev god mode: soak every hit (the shell already marked the player dead) and
    // re-grant grace, so an enemy can be studied without fighting it.
    if (this.devGod) {
      this.player.alive = true;
      this.player.health = 1;
      this.graceUntil = this.now + 1.0;
      return;
    }
    // a Bulwark shield soaks the hit instead of costing a life
    if (this.shields > 0) {
      this.shields -= 1;
      this.tookHitThisLayer = true;
      this.graceUntil = this.now + 1.5;
      // the hit set the 1-HP player tank to dead in bullets.ts; the shield soaked
      // it, so revive in place — otherwise the shell loop skips the player forever
      // (!t.alive) and the floor becomes impossible to lose.
      this.player.alive = true;
      this.player.health = 1;
      this.effects.shieldPop(this.player.pos.x, this.player.floorY + 10, this.player.pos.z, this.player.floorY); // blue shield flare
      this.hud.flashBanner("SHIELD!", 1100);
      sfx.play("deny");
      return;
    }
    this.lives -= 1;
    sfx.play("hurt");
    this.tookHitThisLayer = true;
    // the player tank bursts into cubes on a real hit (radial)
    this.effects.gib(this.player.pos.x, this.player.floorY + 6, this.player.pos.z, this.player.floorY, 0, 0, this.player.bodyColorHex());
    this.deathMarks.drop(this.player.pos.x, this.player.pos.z, this.player.floorY); // marks where you fell — persists onto the retry of this floor
    if (this.lives <= 0) { void this.gameOver(); return; }
    this.hud.flashBanner(`HIT! ${this.lives} ${this.lives === 1 ? "LIFE" : "LIVES"} LEFT`, 1600);
    // restart the current layer fresh (authentic Wii Tanks)
    this.bullets.clear();
    this.enterLayer();
  }

  private async gameOver(): Promise<void> {
    const card = this.cardMode;
    this.mode = "gameover";
    sfx.play("gameover");
    this.bullets.clear();
    this.enemies.clear();
    this.enemies.clearAllies();
    // leave the death-gib cubes mid-air (they fade on their own) behind the panel
    this.clearPickup();
    this.aimLine.hide();
    if (this.session) { void clearGameState(this.session, card ? "cardmode" : "self"); }
    if (card) this.cardSavedState = null; else this.savedState = null;
    this.exitCardMode(); // restore the player tank + drop the card UI
    this.saveProfile();
    const p = this.profileStore?.profile;
    const line = card
      ? `card mode · ${this.runKills} tanks destroyed`
      : (p ? `best climb: layer ${p.highestLayer} · ${p.totalKills} tanks all-time` : "");
    this.hud.showGameOver(this.layer, this.runKills, this.score, line);
    log.info("game", `over at ${card ? "card " : ""}layer ${this.layer}, ${this.runKills} kills, ${this.score} pts`);
  }

  private saveProfile(): void {
    const ps = this.profileStore;
    if (!ps || this.tainted) return; // dev-tainted run: contributes nothing to the profile
    ps.profile.highestLayer = Math.max(ps.profile.highestLayer, this.layer);
    ps.profile.achievements = this.achievements.unlockedIds();
    void ps.saveNow();
  }

  // ---- mutuals -------------------------------------------------------

  private async loadMutuals(handle: string): Promise<void> {
    try {
      const result = await fetchMutualFaces(handle, (m) => this.hud.setStatus(m));
      this.hud.setStatus("");
      if (result.faces.length === 0) { this.hud.flashBanner("no mutuals found", 1500); return; }
      const names = result.faces.map((f) => f.name);
      this.enemies.setMutualNames(names);
      this.hud.flashBanner(`${names.length} mutuals enlisted`, 2200);
      log.info("bsky", `@${handle}: ${result.mutualCount} mutuals enlisted`);
    } catch (e) {
      this.hud.setStatus("");
      log.warn("bsky", "mutual load failed", String(e));
    }
  }

  // ---- loop ----------------------------------------------------------

  private frame = (): void => {
    const t = performance.now();
    let delta = (t - this.lastTime) / 1000;
    this.lastTime = t;
    // a single variable-timestep update per frame — smooth at any refresh rate
    if (delta > 1 / 30) delta = 1 / 30;

    // the game clock must NOT advance while frozen (paused / drafting / mid-fade),
    // or the spawn-grace + layer timers would expire while the player sits in a menu
    const live = this.mode === "playing" && !this.paused && !this.drafting && !this.transitioning;
    if (live) {
      // slow the gameplay sim (tanks/shells/timers) for a more deliberate pace,
      // but drive camera input at real time so the view stays crisp.
      const sim = delta * SIM_SPEED;
      this.now += sim;
      this.updateCameraControls(delta);
      if (this.cardMode) this.updateCard(sim); else this.update(sim);
    }
    else if (this.mode === "menu" && this.demoActive) {
      this.updateDemo(delta * SIM_SPEED); // run the attract demo behind the menu overlay
    }
    else sfx.laserHum(false); // paused / drafting / transitioning / game-over: enemies.update isn't running, so kill the laser hum
    if (this.ascending) this.updateAscend(delta); // elevator rise (during transition)

    if (this.booted) {
      this.world.setTimeOfDay(this.now); // day/night uses the freeze-aware game clock (holds still while paused)
      if (this.mode === "playing" && this.cardMode) {
        // free-pan camera over the battlefield (no player to follow)
        this.world.follow(this.cardFocus, delta, this.cameraYaw, this.cameraZoom * this.layerZoom);
        sfx.setListener(this.cardFocus.x, this.cardFocus.y, this.cardFocus.z);
      } else if (this.mode === "playing") {
        // focus on the player's CURRENT height (rises with the elevator)
        this.focus.set(this.player.pos.x, this.player.floorY, this.player.pos.z);
        this.world.follow(this.focus, delta, this.cameraYaw, this.cameraZoom * this.layerZoom);
        sfx.setListener(this.focus.x, this.focus.y, this.focus.z); // positional audio referenced to the camera
      }
      this.world.updateClouds(delta); // cosmetic drift (real-time; fine while paused) — clouds are world-fixed
      this.arena?.tickWater(delta); // animate the river surface (one uniform write)
      this.updateHud();
    }
    this.updateTankLoops();
    this.world.render();
    this.input.endFrame();
    requestAnimationFrame(this.frame);
  };

  /**
   * Continuous tank loops (tread clatter + turret servo), driven once per frame.
   * EVERY tank emits them — except ghosts (silent) — but to avoid N looping voices
   * stacking into a wall of noise (and the comb-filtering of summing the same loop
   * many times), we voice only TWO sources per sound: the player's OWN tank, plus the
   * NEAREST OTHER moving / turret-rotating tank on the field (positional). So you always
   * hear yourself AND whichever enemy/ally is closest, uniformly across normal + card mode.
   */
  private updateTankLoops(): void {
    if (!this.booted) {
      sfx.sustain("drive", false); sfx.sustain("driveField", false);
      sfx.sustain("turretspin", false); sfx.sustain("turretspinField", false);
      return;
    }
    // loops only while actually playing — never in menus / pause / drafting / mid-fade
    const live = this.mode === "playing" && !this.paused && !this.drafting && !this.transitioning;

    // --- the player's own tank (normal mode only; in card mode it's hidden + parked) ---
    const pActive = live && !this.cardMode && this.player.alive && !this.player.riding;
    sfx.sustain("drive", pActive && this.driving, this.player.pos);
    sfx.sustain("turretspin", pActive && this.turretSpinning, this.player.pos);

    // --- nearest OTHER tank (enemies + allies + card-deployed), so the battlefield is audible ---
    const lx = this.cardMode ? this.cardFocus.x : this.focus.x; // listener = camera focus (set earlier this frame)
    const lz = this.cardMode ? this.cardFocus.z : this.focus.z;
    let drivePos: THREE.Vector3 | null = null, driveD = Infinity;
    let spinPos: THREE.Vector3 | null = null, spinD = Infinity;
    const MOVE_EPS2 = 0.02 * 0.02, TURRET_EPS = 0.02; // per-frame thresholds (turret > player's 0.01 to kill aim micro-jitter)

    const scan = (t: Tank): void => {
      const moved = t.sfxInit && (t.pos.x - t.sfxPrevX) ** 2 + (t.pos.z - t.sfxPrevZ) ** 2 > MOVE_EPS2;
      let dy = t.turretYaw - t.sfxPrevTurret;
      dy = Math.atan2(Math.sin(dy), Math.cos(dy)); // shortest-angle, handles wrap
      const spinning = t.sfxInit && Math.abs(dy) > TURRET_EPS;
      // advance prev-state for EVERY tank, every frame — no stale delta spike after respawn / array churn / unpause
      t.sfxPrevX = t.pos.x; t.sfxPrevZ = t.pos.z; t.sfxPrevTurret = t.turretYaw; t.sfxInit = true;
      if (!live || !t.alive || t.type?.ghost === true || t.riding || t.thrown) return; // silent (prev already advanced)
      const d = (t.pos.x - lx) ** 2 + (t.pos.z - lz) ** 2; // squared dist — ranking only
      if (moved && d < driveD) { driveD = d; drivePos = t.pos; }
      if (spinning && d < spinD) { spinD = d; spinPos = t.pos; }
    };
    for (const e of this.enemies.enemies) scan(e);
    for (const a of this.enemies.allies) scan(a);

    sfx.sustain("driveField", !!drivePos, drivePos ?? undefined);
    sfx.sustain("turretspinField", !!spinPos, spinPos ?? undefined);
  }

  /** Mouse wheel / I,O = zoom; Q/E = orbit the camera. */
  private updateCameraControls(dt: number): void {
    const wheel = this.input.consumeWheel();
    if (wheel !== 0) this.cameraZoom += wheel * CAMERA.zoomStep;
    if (this.input.isDown("KeyI")) this.cameraZoom -= CAMERA.zoomKeyRate * dt; // I = zoom in (closer)
    if (this.input.isDown("KeyO")) this.cameraZoom += CAMERA.zoomKeyRate * dt; // O = zoom out (farther)
    this.cameraZoom = Math.max(CAMERA.minZoom, Math.min(CAMERA.maxZoom, this.cameraZoom));
    let rot = 0;
    if (this.input.isDown("KeyQ")) rot += 1;
    if (this.input.isDown("KeyE")) rot -= 1;
    if (rot !== 0) this.cameraYaw += rot * CAMERA.rotateSpeed * dt;
    // CARD MODE: WASD / arrows free-pan the camera over the arena (no player to follow)
    if (this.cardMode && this.mode === "playing") {
      const mv = this.input.moveAxis();
      if (mv.x !== 0 || mv.z !== 0) {
        const c = Math.cos(this.cameraYaw), s = Math.sin(this.cameraYaw);
        const wx = mv.x * c + mv.z * s, wz = -mv.x * s + mv.z * c;
        const pan = 420 * this.cameraZoom * dt; // pan faster when zoomed out
        this.cardFocus.x += wx * pan; this.cardFocus.z += wz * pan;
        clampInterior(this.cardFocus, 0); // keep the view roughly within the arena
      }
    }
  }

  private update(dt: number): void {
    if (this.transitioning) return; // mid elevator fade — freeze the sim

    // --- player control (camera-relative: W drives "up the screen") ---
    this.prevPlayer.copy(this.player.pos);
    if (this.player.riding) {
      // mid lift-ride: the tank is carried between levels; freeze input. Ride at
      // REAL time (un-slow the dt) so lifts feel as snappy as the floor elevator.
      this.player.advanceRide(dt / SIM_SPEED);
      this.playerVel.set(0, 0, 0);
      this.driving = false; this.turretSpinning = false; // no engine/servo sfx mid-ride
      this.aimLine.hide();
    } else {
      const mv = this.input.moveAxis();
      this.driving = mv.x !== 0 || mv.z !== 0; // holding a direction = treads rolling
      if (mv.x !== 0 || mv.z !== 0) {
        const c = Math.cos(this.cameraYaw), s = Math.sin(this.cameraYaw);
        let wx = mv.x * c + mv.z * s;
        let wz = -mv.x * s + mv.z * c;
        const len = Math.hypot(wx, wz);
        wx /= len; wz /= len;
        this.player.pos.x += wx * TANK.moveSpeed * this.up.speedMul * dt;
        this.player.pos.z += wz * TANK.moveSpeed * this.up.speedMul * dt;
        this.player.bodyYaw = approachAngle(this.player.bodyYaw, yawToward(wx, wz), TANK.turnSpeed * 1.6 * this.up.turnSpeedMul * dt);
      }
      resolveCircle(this.player.pos, TANK.radius, this.player.level === 0); // perimeter (+ ground solids)
      if (this.arena) {
        // roads have NO railings: drive off a deck edge → step DOWN to whatever deck/ground is beneath
        this.player.level = this.arena.supportLevel(this.player.pos.x, this.player.pos.z, this.player.level);
        this.player.floorY = this.floorY + this.arena.levelHeight(this.player.level);
      }
      clampInterior(this.player.pos, TANK.radius); // safety net: never strand the player out of bounds
      this.playerVel.set((this.player.pos.x - this.prevPlayer.x) / dt, 0, (this.player.pos.z - this.prevPlayer.z) / dt);

      // turret aims at the mouse on the player's level plane
      const ndc = this.input.ndc();
      const prevTurret = this.player.turretYaw;
      if (this.world.screenToGround(ndc.x, ndc.y, this.player.floorY, this.aimPoint)) {
        const want = yawToward(this.aimPoint.x - this.player.pos.x, this.aimPoint.z - this.player.pos.z);
        this.player.turretYaw = approachAngle(this.player.turretYaw, want, TANK.turretTurn * dt);
      }
      // servo whir while the turret actually swings (shortest-angle delta past a jitter floor)
      let dyaw = this.player.turretYaw - prevTurret;
      dyaw = Math.atan2(Math.sin(dyaw), Math.cos(dyaw));
      this.turretSpinning = Math.abs(dyaw) > 0.01;
      this.player.sync();

      // shot-trajectory preview (traces ricochets) — helps plan bank shots
      const mz = this.player.muzzle(this.aimMuzzle);
      const fw = this.player.forward(this.aimFwd);
      const range = BULLET.life * BULLET.speed * this.up.shellSpeedMul;
      const shellStep = (BULLET.speed * this.up.shellSpeedMul * dt) / 2; // matches the shell's per-substep stride (vel·dt / 2 substeps)
      this.aimLine.update(mz.x, mz.y, mz.z, fw.x, fw.z, this.player.level, this.up.maxBounces, range, this.arena, shellStep);

      // fire (upgrade levers: shell speed / bounces / cooldown / max shells)
      this.fireCooldown -= dt;
      if (this.input.fireHeld && this.fireCooldown <= 0 && this.bullets.countShells(this.player) < this.up.maxShells) {
        this.bullets.fire(this.player, BULLET.speed * this.up.shellSpeedMul, this.up.maxBounces, false, BULLET.life, this.up.damage, false, undefined, undefined, this.up.shellHp);
        this.fireCooldown = BULLET.cooldown * this.up.cooldownMul;
        this.noFireTimer = 0;
        sfx.play("fire");
      }
      // mine
      if (this.input.consumeMine() && this.bullets.countMines(this.player) < this.up.maxMines) {
        this.bullets.layMine(this.player, false, this.up.mineRadiusMul);
        sfx.play("throw");
      }

      // prompt the first time the player rolls onto a lift
      const onLift = !!this.arena?.liftAt(this.player.pos.x, this.player.pos.z, this.player.level);
      if (onLift && !this.wasOnLift) this.hud.flashBanner("PRESS F TO RIDE ↕", 1400);
      this.wasOnLift = onLift;

      // pacifist achievement timer
      this.noFireTimer += dt;
      if (this.noFireTimer >= 20 && this.phase === "combat") this.achievements.unlock("pacifist_dodge");
    }

    // leave tread marks as the player drives (distance-gated; one draw call total)
    if (!this.player.riding) {
      const tdx = this.player.pos.x - this.player.treadX, tdz = this.player.pos.z - this.player.treadZ;
      if (tdx * tdx + tdz * tdz >= TREAD_STEP2) {
        this.treads.drop(this.player.pos.x, this.player.pos.z, this.player.floorY, this.player.bodyYaw);
        this.player.treadX = this.player.pos.x; this.player.treadZ = this.player.pos.z;
      }
    }

    // --- effects + bonus pickup ---
    this.effects.update(dt);
    this.treads.update();
    this.deathMarks.update();
    this.updatePickup(dt);

    // --- enemies + bullets ---
    if (this.phase === "combat" || this.phase === "cleared") {
      // enemies treat the player AND every hired mercenary as a threat — they path
      // toward and aim at whichever is nearest (mercs are first-class targets).
      const threats: Tank[] = [this.player, ...this.enemies.allies];
      this.enemies.update(dt, this.player, this.playerVel, this.bullets, this.arena, threats);
      this.enemies.updateAllies(dt, this.player, this.bullets, this.arena); // hired mercenaries fight on team 0
      this.tickMercRespawn(dt);
      if (this.enemies.onFieldCount >= 8) this.achievements.unlock("full_house");

      const vulnerable = this.phase === "combat" && this.now >= this.graceUntil;
      // allies are always valid targets (enemy fire can hit them); the player only when vulnerable
      const targets: Tank[] = vulnerable ? [this.player, ...this.enemies.allies, ...this.enemies.enemies] : [...this.enemies.allies, ...this.enemies.enemies];
      const events = this.bullets.update(dt, targets);
      // Process enemy destructions first; defer the player's life-loss to AFTER
      // the loop so the layer-reset it triggers can't corrupt the remaining
      // events (which still reference this layer's now-destroyed tanks).
      let playerHit = false, playerHitMine = false;
      for (const ev of events) {
        if (ev.tank === this.player) { playerHit = true; if (ev.mine) playerHitMine = true; }
        else if (ev.shotDown) {
          // the player shot an enemy shell/missile out of the air — score it (no kill, no gib)
          this.awardShootdown(ev);
        } else if (ev.absorbed) {
          // an enemy shield soaked the hit (sound + blue flare already fired) — score the player for cracking it
          if (ev.byPlayer) this.awardShieldPop(ev);
        } else if (ev.leg) {
          // the player (or a merc, at ¼) chipped a mecha's leg — score it; the walker lives
          if (ev.byPlayer) this.awardLeg(ev, 1);
          else if (ev.killer && ev.killer.team === 0) this.awardLeg(ev, 0.25);
        } else if (ev.tank.team === 0) {
          // a hired mercenary was destroyed — no score. The gib / sound / redeploy fire
          // uniformly when it's culled (enemies.onMercDown), so laser + blast deaths count too.
        } else {
          // a destroyed enemy bursts into colour-matched cubes, flung along the shot
          this.effects.gib(ev.tank.pos.x, ev.tank.floorY + 6, ev.tank.pos.z, ev.tank.floorY, ev.dirx, ev.dirz, ev.tank.bodyColorHex());
          this.deathMarks.drop(ev.tank.pos.x, ev.tank.pos.z, ev.tank.floorY); // permanent white X at the kill site
          this.onEnemyDestroyed(ev);
        }
      }
      const laserBurn = this.enemies.consumePlayerLaser(); // a laser beam burned the player this frame
      if (playerHit) {
        if (playerHitMine) this.minesHitThisLayer++;
        this.loseLife();
      } else if (laserBurn && this.now >= this.graceUntil) {
        this.loseLife(); // deferred, grace-gated (loseLife re-arms grace so a beam can't chain-drain)
      }
    }

    // layer cleared?
    if (this.phase === "combat" && this.enemies.layerCleared) this.onLayerCleared();

    // ride the elevator (this layer's EXIT hole) — edge-triggered on driving onto
    // it, only from the ground (you must lift back down off a road first)
    const onElev = this.player.level === 0 && !this.player.riding &&
      isOverHole(this.player.pos.x, this.player.pos.z, !this.entryIsA(this.layer));
    if (this.phase === "cleared" && onElev && !this.wasOnElevator) this.rideElevator();
    this.wasOnElevator = onElev;
  }

  private onEnemyDestroyed(ev: HitEvent): void {
    sfx.play("explode");
    this.runKills++;
    if (this.profileStore && !this.tainted) {
      const t = (this.profileStore.profile.totalKills += 1);
      if (t >= 100) this.achievements.unlock("kills_100");
      if (t >= 1000) this.achievements.unlock("kills_1k");
      if (t >= 10000) this.achievements.unlock("kills_10k");
      if (t >= 100000) this.achievements.unlock("kills_100k");
      this.profileStore.save();
    }
    if (ev.byPlayer) {
      if (ev.ricochet) this.achievements.unlock("ricochet");
      if (ev.mine) this.achievements.unlock("mine_kill");
      const d = Math.hypot(ev.tank.pos.x - this.player.pos.x, ev.tank.pos.z - this.player.pos.z);
      if (d < 34) this.achievements.unlock("point_blank");
      if (this.now - this.lastKillTime < 0.5) this.achievements.unlock("double");
      this.lastKillTime = this.now;
      this.awardPoints(ev);
    } else if (ev.killer && ev.killer.team === 0) {
      this.awardPoints(ev, 0.25); // a hired merc's kill — quarter points, so killing things yourself stays the top earner
    }
  }

  /** Score a kill: base + distance, multiplied by ricochets, with a combo toast. `factor`
   *  scales the payout (1 = the player's own kill; 0.25 = a hired mercenary's kill). */
  private awardPoints(ev: HitEvent, factor = 1): void {
    const raw = 100 + Math.round(ev.traveled * 0.5); // longer shots are worth more
    const mult = 1 + ev.bounces; // each ricochet doubles up
    const pts = Math.max(1, Math.round(raw * mult * factor));
    this.score += pts;
    // describe the shot — bounce tier first, then a long-shot tag, joined into a combo
    const tags: string[] = [];
    if (factor < 1) tags.push("ALLY KILL");
    if (ev.mine) tags.push("MINE KILL");
    if (ev.bounces > 0) tags.push(BOUNCE_NAMES[ev.bounces] ?? `${ev.bounces}× BANK`);
    if (ev.traveled >= 640) tags.push("MOONSHOT");
    else if (ev.traveled >= 340) tags.push("LONG SHOT");
    this.hud.addScore(this.score, mult, tags.join(" "));
  }

  /** Score a hit on a mecha's leg (drained shield or popped leg) — a partial hit worth less
   *  than a kill; bounce multiplier applies. `factor` 1 = player, 0.25 = a merc's hit. */
  private awardLeg(ev: HitEvent, factor = 1): void {
    const mult = 1 + ev.bounces;
    const pts = Math.max(1, Math.round((30 + Math.round(ev.traveled * 0.25)) * mult * factor));
    this.score += pts;
    this.hud.addScore(this.score, mult, factor < 1 ? "ALLY LEG HIT" : "LEG HIT");
  }

  /** Score the player for shooting an enemy shell (or TOW missile) out of the air. */
  private awardShootdown(ev: HitEvent): void {
    const pts = ev.tow ? 75 : 40;
    this.score += pts;
    this.hud.addScore(this.score, 1, ev.tow ? "MISSILE DOWN" : "SHOT DOWN");
  }

  /** Score the player for cracking an enemy's shield — a partial hit, worth less
   *  than a kill, but still rewards the bank shot (bounce multiplier applies). */
  private awardShieldPop(ev: HitEvent): void {
    const mult = 1 + ev.bounces;
    const pts = (25 + Math.round(ev.traveled * 0.25)) * mult * (ev.popped ?? 1); // Punch cracks several shields → scaled
    this.score += pts;
    this.hud.addScore(this.score, mult, "SHIELD BREAK");
  }

  private updateHud(): void {
    if (this.cardMode && this.mode === "playing") { this.updateCardHud(); return; }
    const s = this.hudState;
    s.layer = this.layer;
    s.lives = this.lives;
    s.maxLives = this.maxLives;
    s.shields = this.shields;
    s.tanksLeft = this.mode === "playing" ? this.enemies.aliveCount : 0;
    this.hud.update(s);
    if (debug.visible) {
      debug.watch("mode", this.mode);
      debug.watch("phase", this.phase);
      debug.watch("layer", this.layer);
      debug.watch("enemies", this.enemies.aliveCount);
      debug.watch("shells", this.bullets.shells.length);
      debug.render();
    }
  }
}

const container = document.getElementById("app")!;
const game = new Game(container);
(window as unknown as { GAME: unknown }).GAME = Object.assign(game, { log, debug, sfx, music, THREE });
log.info("boot", "TANKS — sign in with Bluesky. WASD drive, mouse aim, click fire, Space mine. M = mute.");
